import React, { useState, useEffect, useRef } from 'react';
import apiClient from '@/api'; // Your API client from api.js

const MessageBubble = ({ message, showHeader }) => {
    const formatTime = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className={`message-wrapper ${message.position}`}>
            {showHeader && message.position === 'left' && (
                <div className="message-header">
                    <span className="sender-name">{message.sender_name}</span>
                </div>
            )}
            <div className="message-content-row">
                <div className={`message-bubble ${message.position} ${!showHeader ? 'no-timestamp' : ''}`}>
                    <p className="message-text">{message.text}</p>
                </div>
                {showHeader && (
                    <span className="message-time">
                        {formatTime(message.date)}
                    </span>
                )}
            </div>
        </div>
    );
};

export default function MessageWindow({ threadId }) {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const groupMessages = (msgs) => {
        return msgs.map((message, index) => {
            const prevMessage = msgs[index - 1];
            const isSameSender = prevMessage && prevMessage.sender_name === message.sender_name;
            const timeDiff = prevMessage ? 
                (new Date(message.created_at) - new Date(prevMessage.created_at)) : 
                Infinity;
            const isWithinTimeFrame = timeDiff < 120000; // 2 minutes

            return {
                ...message,
                showHeader: !isSameSender || !isWithinTimeFrame
            };
        });
    };

    useEffect(() => {
        if (!threadId) return;

        const loadMessages = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get(`/messages/${threadId}`);
                const data = response.data;
                const messagesArray = Array.isArray(data) ? data : (data.messages || []);
                
                const formattedMessages = messagesArray.map(msg => ({
                    id: msg.id,
                    position: msg.position,
                    text: msg.body,
                    date: new Date(msg.created_at),
                    sender_name: msg.sender_name,
                    created_at: msg.created_at
                }));

                setMessages(groupMessages(formattedMessages));
            } catch (error) {
                console.error('Error loading messages:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadMessages();
        const interval = setInterval(loadMessages, 15000); // Poll every 15 seconds

        return () => clearInterval(interval);
    }, [threadId]);

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isSending) return;

        setIsSending(true);
        const messageBody = inputValue;
        setInputValue(''); // Clear immediately for better UX

        try {
            const response = await apiClient.put(`/messages/${threadId}`, { 
                body: messageBody 
            });
            const newMessage = response.data;
            
            const formattedMessage = {
                id: newMessage.id,
                position: newMessage.position,
                text: newMessage.body,
                date: new Date(newMessage.created_at),
                sender_name: newMessage.sender_name,
                created_at: newMessage.created_at
            };

            setMessages(prevMessages => {
                const updated = [...prevMessages, formattedMessage];
                return groupMessages(updated.map(m => ({
                    id: m.id,
                    position: m.position,
                    text: m.text,
                    date: m.date,
                    sender_name: m.sender_name,
                    created_at: m.created_at
                })));
            });
        } catch (error) {
            console.error("Failed to send message:", error);
            setInputValue(messageBody); // Restore input on error
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        
        // Auto-resize textarea
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    };

    if (!threadId) {
        return (
            <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the list to start chatting</p>
            </div>
        );
    }

    return (
        <div className="message-window">
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }

.message-window {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    background-color: #f8f9fa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    overflow: hidden;  /* ADD THIS - prevents the whole window from scrolling */
}

.messages-container {
    flex: 1 1 auto;  /* CHANGE THIS - was just "flex: 1" */
    overflow-y: auto;
    overflow-x: hidden;  /* ADD THIS - prevents horizontal scroll */
    padding: 20px 16px;
    background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
    min-height: 0;  /* ADD THIS - critical for flexbox scrolling to work */
}

                .messages-container::-webkit-scrollbar {
                    width: 6px;
                }

                .messages-container::-webkit-scrollbar-track {
                    background: transparent;
                }

                .messages-container::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 3px;
                }

                .messages-container::-webkit-scrollbar-thumb:hover {
                    background: #a0aec0;
                }

                .message-wrapper {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 2px;
                    animation: slideIn 0.2s ease-out;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .message-wrapper.left {
                    align-items: flex-start;
                }

                .message-wrapper.right {
                    align-items: flex-end;
                }

                .message-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 4px;
                    margin-top: 12px;
                    padding: 0 4px;
                }

                .sender-name {
                    font-weight: 600;
                    font-size: 0.813rem;
                    color: #4a5568;
                }

                .message-content-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 6px;
                    max-width: 100%;
                }

                .message-wrapper.right .message-content-row {
                    flex-direction: row-reverse;
                }

                .message-bubble {
                    max-width: 85%;
                    min-width: 60px;
                    padding: 10px 14px;
                    border-radius: 18px;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                    position: relative;
                    line-height: 1.4;
                    word-wrap: normal;
                    flex-shrink: 1;
                    flex-grow: 0;
                }

                .message-bubble.no-timestamp {
                    max-width: 95%;
                }

                .message-bubble.left {
                    background-color: #ffffff;
                    color: #2d3748;
                    border-bottom-left-radius: 4px;
                    border: 1px solid #e2e8f0;
                }

                .message-bubble.right {
                    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
                    color: white;
                    border-bottom-right-radius: 4px;
                }

                .message-text {
                    margin: 0;
                    font-size: 0.938rem;
                    white-space: pre-wrap;
                    word-wrap: normal;
                    word-break: keep-all;
                    overflow-wrap: normal;
                    hyphens: none;
                    display: inline-block;
                    max-width: 100%;
                }

                .message-time {
                    font-size: 0.688rem;
                    color: #a0aec0;
                    white-space: nowrap;
                    padding: 0 2px;
                    user-select: none;
                    flex-shrink: 0;
                }

                .input-container {
                    background-color: white;
                    border-top: 1px solid #e2e8f0;
                    padding: 16px;
                    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
                }

                .input-wrapper {
                    display: flex;
                    align-items: flex-end;
                    gap: 10px;
                    max-width: 100%;
                }

                .textarea-container {
                    flex: 1;
                    position: relative;
                    background-color: #f7fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 24px;
                    padding: 10px 16px;
                    transition: all 0.2s ease;
                }

                .textarea-container:focus-within {
                    background-color: white;
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
                }

                .message-input {
                    width: 100%;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 0.938rem;
                    font-family: inherit;
                    resize: none;
                    min-height: 24px;
                    max-height: 120px;
                    line-height: 1.5;
                    color: #2d3748;
                }

                .message-input::placeholder {
                    color: #a0aec0;
                }

                .send-button {
                    flex-shrink: 0;
                    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(13, 110, 253, 0.3);
                }

                .send-button:hover:not(:disabled) {
                    transform: scale(1.05);
                    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.4);
                }

                .send-button:active:not(:disabled) {
                    transform: scale(0.95);
                }

                .send-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    background: #cbd5e0;
                    box-shadow: none;
                }

                .send-icon {
                    width: 20px;
                    height: 20px;
                }

                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #a0aec0;
                    text-align: center;
                    padding: 40px;
                }

                .empty-state svg {
                    margin-bottom: 16px;
                    opacity: 0.5;
                }

                .empty-state h3 {
                    font-size: 1.25rem;
                    color: #4a5568;
                    margin-bottom: 8px;
                }

                .empty-state p {
                    font-size: 0.938rem;
                    color: #a0aec0;
                }

                .loading-state {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: #a0aec0;
                }
            `}</style>

            <div className="messages-container">
                {isLoading && messages.length === 0 ? (
                    <div className="loading-state">Loading messages...</div>
                ) : (
                    <>
                        {messages.map((message) => (
                            <MessageBubble
                                key={message.id}
                                message={message}
                                showHeader={message.showHeader}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            <div className="input-container">
                <div className="input-wrapper">
                    <div className="textarea-container">
                        <textarea
                            ref={textareaRef}
                            className="message-input"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Type a message..."
                            rows="1"
                            maxLength={5000}
                        />
                    </div>
                    <button
                        className="send-button"
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || isSending}
                        title="Send message (Enter)"
                    >
                        <svg className="send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
